/**
 * Global axios failures → store notification.
 * Opt out: `{ skipGlobalErrorNotification: true }` on request config.
 */
export function formatAxiosErrorMessage(error) {
    if (!error) return 'Request failed';
    if (error.code === 'ECONNABORTED') return 'Request timed out. Try again.';
    const res = error.response;
    const status = res?.status;
    if (status === 404) return 'Resource not found (404).';
    if (status === 403) return 'Access denied (403).';
    if (status >= 500) return `Server error (${status}). Try again later.`;
    const d = res?.data;
    if (typeof d === 'string' && d.trim()) return d.trim();
    if (d && typeof d === 'object') {
        const msg =
            pickMessageField(d.message) ||
            pickMessageField(d.error) ||
            pickMessageField(d.detail) ||
            (d.errors && formatValidationErrors(d.errors));
        if (msg) return msg;
        if (d.status != null && typeof res.statusText === 'string' && res.statusText) {
            return `${status}: ${res.statusText}`;
        }
    }
    if (error.message) return error.message;
    return 'Request failed';
}

function pickMessageField(value) {
    if (value == null) return null;
    if (Array.isArray(value)) {
        const flattened = value.map((v) => (typeof v === 'string' ? v : pickMessageField(v))).filter(Boolean);
        return flattened.length ? flattened.join('; ') : null;
    }
    if (typeof value === 'object') return pickMessageField(value.message || value.detail);
    return String(value).trim() || null;
}

function formatValidationErrors(errorsObj) {
    if (!errorsObj || typeof errorsObj !== 'object') return null;
    const parts = [];
    for (const key of Object.keys(errorsObj)) {
        const val = errorsObj[key];
        if (Array.isArray(val)) parts.push(`${key}: ${val.join(', ')}`);
        else if (val != null && val !== '') parts.push(`${key}: ${String(val)}`);
    }
    return parts.length ? parts.join('; ') : null;
}

/** Pathname-ish segment for routing rules (suppress noisy auth/session probes). */
function requestPath(error) {
    const cfg = error.config || {};
    const raw = (cfg.url || '').trim();
    if (/^https?:\/\//i.test(raw)) {
        try {
            return new URL(raw).pathname || '';
        } catch {
            return raw.replace(/\\/g, '/').split('?')[0];
        }
    }
    const base = (cfg.baseURL || '').replace(/\/$/, '');
    const joined = `${base}${raw.startsWith('/') ? '' : '/'}${raw}`.replace(/([^:])\/{2,}/g, '$1/');
    if (/^https?:\/\//i.test(joined)) {
        try {
            return new URL(joined).pathname || '';
        } catch {
            return joined.replace(/\\/g, '/').split('?')[0];
        }
    }
    return raw.replace(/\\/g, '/').split('?')[0] || '';
}

function shouldSuppressNotification(error, axiosRef) {
    const cfg = error.config || {};
    if (cfg.skipGlobalErrorNotification) return true;

    if (typeof axiosRef?.isCancel === 'function' && axiosRef.isCancel(error)) return true;

    const status = error.response?.status;
    const pathname = requestPath(error);

    /** Session probe: suppress 401 toasts when the app checks `/me` while logged out */
    if (status === 401 && /(^|\/)me$/i.test(pathname)) return true;

    if (error.code === 'ERR_CANCELED' || error.name === 'CanceledError') return true;

    return false;
}

/**
 * Register once at app bootstrap (same axios instance all services import).
 */
export function installAxiosErrorNotifications(store, axiosInstance) {
    if (!store || typeof store.commit !== 'function' || !axiosInstance) return;

    axiosInstance.interceptors.response.use(
        (response) => response,
        (error) => {
            if (!shouldSuppressNotification(error, axiosInstance)) {
                const message = formatAxiosErrorMessage(error);
                store.commit('notifications/ADD_NOTIFICATION', {
                    title: 'Request failed',
                    text: message,
                    type: 'error',
                    timeout: true,
                    delay: 8000
                });
            }
            return Promise.reject(error);
        }
    );
}
